/* App entry point — initialises auth, registers routes, starts router. */
import { initAuth, onUserChange, signOutUser, currentUser } from "./auth.js";
import { register, start, navigate } from "./router.js";
import { OFFLINE_MODE } from "./firebase-config.js";
import { attachLightboxDelegation } from "./lightbox.js";

// Document-level click delegation for any [data-lightbox] element across
// the whole app — opens an in-app fullscreen viewer instead of leaking
// the firebasestorage.googleapis.com URL via target="_blank". Idempotent
// (guarded inside the helper) so per-view re-attachment is safe.
attachLightboxDelegation(document);

// View modules — split between static (boot-critical) and lazy (loaded
// on first visit). Static set covers: landing/signin (every cold-start
// visitor), home (post-sign-in landing), notfound (router fallback),
// legal pages (small + linked from footer/landing). Everything else is
// dynamic-imported on demand so the entry bundle stays small.
import { renderHome }      from "./views/home.js";
import { renderAuthView }  from "./views/auth.js";
import { renderLanding }   from "./views/landing.js";
import { renderNotFound }  from "./views/notfound.js";
import { renderPrivacy, renderRefunds, renderCancellation } from "./views/legal.js";
import { isAdmin, loadAllEdits, primeAdminClaim, resetAdminClaim } from "./admin.js";
import { loadSubscription } from "./subscription.js";
import { primeAvatar, renderChip, resetAvatar } from "./avatar.js";
import { initInstallPrompt } from "./install-prompt.js";
import { startAutoUpdate } from "./auto-update.js";
import "./theme.js"; // Imported for side-effect: applies persisted theme before first paint.
import * as presence from "./presence.js";
import { initErrorTracking, setErrorUser, trackEvent } from "./error-tracking.js";

/** Wrap a dynamic-import view loader in the shape the router expects.
 *  The router calls `handler(view, params)` synchronously; we return
 *  an async wrapper that fetches the chunk on first hit (cached by
 *  the browser thereafter) and then invokes the real renderer.
 *
 *  IMPORTANT: the import() at each call site MUST use a static string
 *  literal — that's what esbuild's code-splitter analyzes at build time
 *  to emit a chunk. Passing a path as a VARIABLE produces a runtime
 *  fetch for the literal string (e.g. "./views/practice.js"), which
 *  doesn't exist in production because only the bundled chunks are
 *  deployed; firebase.json's catchall rewrite then serves /index.html
 *  to the missing-asset request and the browser fails to parse HTML
 *  as a JS module. That's the "Couldn't load that page" trap. Hence
 *  the `loader` parameter is a thunk like `() => import("./views/x.js")`
 *  rather than a string path.
 */
function lazy(loader, exportName) {
  return async (view, params) => {
    // Defer the "Loading…" paint by 100ms — if the chunk is already
    // cached (typical for repeat visits within a session), the import
    // resolves on the next microtask and the loader never appears.
    // Only slow first-fetches over a real network get the loader card.
    let loaderTimer = setTimeout(() => {
      view.innerHTML = `<section class="card"><p class="muted small">Loading…</p></section>`;
    }, 100);
    try {
      const mod = await loader();
      clearTimeout(loaderTimer);
      const fn = mod[exportName];
      if (typeof fn !== "function") throw new Error(`Missing export ${exportName}`);
      return fn(view, params);
    } catch (err) {
      clearTimeout(loaderTimer);
      console.error("[router] lazy load failed:", exportName, err);
      // Capture the route at error time so the Reload button can put the
      // user back on the EXACT same view they tried to visit, even if the
      // boot sequence races and the hash drifts. Without this, reloads
      // were perceived as "bouncing to home" — depending on auth-state
      // timing the SPA could reach steady state on /home before the
      // freshly-imported chunk resolved.
      const routeAtError = location.hash || "#/home";
      const safeRoute = routeAtError.replace(/[<>"']/g, "");

      // Helper: force the active service worker to update + activate any
      // waiting SW before reloading. The classic "Couldn't load that page"
      // failure mode is: old PWA tab running old entry bundle baked with
      // old chunk hashes; deploy wiped those chunks from hosting; nav
      // click → 404 → catch-all serves index.html → MIME fail. The cure
      // is to swap in the new SW (which precached the new entry) BEFORE
      // reloading, so the reload renders the new app.
      const swReloadThenNavigate = async () => {
        try {
          const sw = navigator.serviceWorker;
          const reg = await sw?.getRegistration();
          if (sw && reg) {
            // If a new SW is installing/waiting, wait (bounded) for it to
            // take over BEFORE reloading. Reloading on the same tick as
            // the SKIP_WAITING nudge races activation — the old SW is
            // still controlling, serves the stale cached shell, and the
            // chunk error just repeats (burning our once-per-route
            // retry). controllerchange is the "new SW owns the page"
            // signal; the 4s cap keeps us from hanging if it never comes.
            const takeover = new Promise((resolve) => {
              const done = () => { sw.removeEventListener("controllerchange", done); resolve(); };
              sw.addEventListener("controllerchange", done);
            });
            await reg.update().catch(() => {});
            if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
            if (reg.waiting || reg.installing) {
              await Promise.race([takeover, new Promise((r) => setTimeout(r, 4000))]);
            }
          }
        } catch (e) { /* ignore — best-effort */ }
        if (location.hash !== safeRoute) {
          history.replaceState(null, "", location.pathname + location.search + safeRoute);
        }
        location.reload();
      };

      // Auto-recover: if the failure smells like a stale-bundle chunk
      // fetch (most common cause on mobile after a fresh deploy), try
      // ONCE per route per session to refresh the SW + reload. The
      // sessionStorage flag prevents an infinite reload loop if the
      // failure is something else (genuine network outage, bug in the
      // chunk itself, etc.) — we'll fall through to the error card on
      // the second attempt.
      const errStr = String((err && (err.message || err.stack)) || err);
      const isChunkError = /failed to fetch dynamically imported module|importing a module script failed|loading chunk|loading css chunk|error loading dynamically imported module/i.test(errStr);
      const sessionKey = `sv:lazy-reload:${safeRoute}`;
      if (isChunkError && typeof sessionStorage !== "undefined" && !sessionStorage.getItem(sessionKey)) {
        // The flag MUST persist before we reload — it's the only thing
        // breaking the reload loop (in-memory state dies with the page).
        // If setItem throws (quota lockdown, old-Safari private mode),
        // skip the auto-reload entirely and fall through to the error
        // card rather than risk reloading forever.
        let flagPersisted = false;
        try {
          sessionStorage.setItem(sessionKey, String(Date.now()));
          flagPersisted = true;
        } catch { /* private mode — no loop-breaker, so no auto-reload */ }
        if (flagPersisted) {
          // Show a quick toast so the user knows we're recovering, not
          // just bouncing them around silently.
          try { window.toast?.("Updating app — one sec…", "info"); } catch {}
          swReloadThenNavigate();
          return;
        }
      }

      view.innerHTML = `
        <section class="card">
          <h2>Couldn't load that page</h2>
          <p class="muted">Network hiccup or a stale tab during a deploy. Reload to try again — you'll come right back to this view.</p>
          <div class="account-actions" style="margin-top:12px;display:flex;gap:8px;flex-wrap:wrap;">
            <button class="btn btn-primary" id="lazy-reload">Reload</button>
            <a class="btn btn-ghost" href="#/home">Go home</a>
          </div>
        </section>`;
      const reloadBtn = view.querySelector("#lazy-reload");
      if (reloadBtn) {
        reloadBtn.addEventListener("click", () => {
          // Clear the session-retry flag so the user gets one more
          // automatic-retry next time they hit a chunk error on this
          // route — manual reload signals "yes, try the recovery path
          // again."
          try { sessionStorage.removeItem(sessionKey); } catch {}
          swReloadThenNavigate();
        });
      }
    }
  };
}

// Fire Sentry init early — before anything else can throw, so unhandled
// errors during boot still surface. Lazy-loads the SDK so a no-DSN build
// doesn't pay a network cost.
initErrorTracking();

// Eager routes — bundled into the entry chunk.
register("/home",         renderHome);
register("/signin",       renderAuthView);
register("/landing",      renderLanding);
register("/notfound",     renderNotFound);
register("/privacy",      renderPrivacy);
register("/refunds",      renderRefunds);
register("/cancellation", renderCancellation);

// Lazy routes — each `import("./views/x.js")` is a static string literal
// that esbuild's code-splitter analyzes at build time to emit a chunk.
// Do NOT factor these out to a variable or a wrapper — esbuild needs to
// see the literal at the call site or it falls back to a runtime fetch
// for the source path, which 404s in production (only chunks deployed).
register("/practice",          lazy(() => import("./views/practice.js"),     "renderPractice"));
register("/review",            lazy(() => import("./views/review.js"),       "renderReview"));
register("/exam",              lazy(() => import("./views/exam.js"),         "renderExam"));
register("/exam/result",       lazy(() => import("./views/exam.js"),         "renderExamResult"));
register("/dashboard",         lazy(() => import("./views/dashboard.js"),    "renderDashboard"));
register("/achievements",      lazy(() => import("./views/achievements.js"), "renderAchievements"));
register("/multiplayer",       lazy(() => import("./views/multiplayer.js"),  "renderMultiplayer"));
register("/admin",             lazy(() => import("./views/admin-hub.js"),    "renderAdminHub"));
register("/admin/bank",        lazy(() => import("./views/admin.js"),        "renderBankConsole"));
register("/admin/bulk-images", lazy(() => import("./views/bulk-images.js"),  "renderBulkImages"));
register("/admin/users",       lazy(() => import("./views/admin-users.js"),  "renderAdminUsers"));
register("/admin/analytics",   lazy(() => import("./views/admin-analytics.js"), "renderAdminAnalytics"));
register("/admin/daily-content", lazy(() => import("./views/admin-daily-content.js"), "renderAdminDailyContent"));
register("/account",           lazy(() => import("./views/account.js"),      "renderAccount"));
register("/upgrade",           lazy(() => import("./views/upgrade.js"),      "renderUpgrade"));
register("/daily",             lazy(() => import("./views/daily.js"),        "renderDailyView"));
register("/flashcards",        lazy(() => import("./views/flashcards.js"),   "renderFlashcards"));
register("/welcome-diagnostic", lazy(() => import("./views/welcome-diagnostic.js"), "renderWelcomeDiagnostic"));

const chip    = document.getElementById("user-chip");
const signout = document.getElementById("signout-btn");
const nav     = document.getElementById("bottomnav");

signout.addEventListener("click", async () => {
  await signOutUser();
  navigate("/signin");
});

const adminLink = document.getElementById("admin-link");

// Routes a logged-out user is allowed to see. Anything else gets redirected
// to the marketing landing page by the hashchange auth-guard below.
const PUBLIC_ROUTES = new Set(["/landing", "/signin"]);

onUserChange((user) => {
  // Tag every subsequent error with the signed-in user (or clear on
  // sign-out). Sentry batches the user context in memory until the SDK
  // is ready, so this is safe to call before initErrorTracking resolves.
  setErrorUser(user ? { uid: user.uid, email: user.email } : null);

  if (user) {
    trackEvent("auth.signed-in");
    // Reset the cached admin custom claim BEFORE any isAdmin() call
    // for the new user (review #11). Without this, signing out of an
    // admin account and immediately signing into a non-admin one would
    // leave _hasAdminClaim=true until primeAdminClaim() resolved
    // ~hundreds of ms later, so the new user briefly saw the admin
    // nav link + isAdmin() returned true on any synchronous check
    // during that window. The fresh prime below will repopulate it
    // with the new user's actual claim.
    resetAdminClaim();
    // Paint the chip immediately with the default avatar — primeAvatar() will
    // upgrade it once the real one comes back from Firestore.
    document.body.classList.remove("signed-out");
    renderChip();
    signout.classList.remove("hidden");
    nav.classList.remove("hidden");
    if (adminLink) adminLink.classList.toggle("hidden", !isAdmin(user));
    // Pull the admin custom claim from the user's ID token. For
    // bootstrap admins this is a no-op (isAdmin is already true via
    // email); for dynamically-promoted admins this is what flips the
    // chip and the rules-side checks. Re-evaluate the admin link
    // visibility once the claim resolves so a freshly-promoted admin
    // sees the link without manually reloading.
    primeAdminClaim()
      .then(() => {
        if (adminLink) adminLink.classList.toggle("hidden", !isAdmin(user));
      })
      .catch((e) => console.warn("[app] primeAdminClaim:", e));
    // Fire off the edit fetch in the background — data.js will await it.
    loadAllEdits().catch((e) => console.warn("[app] loadAllEdits:", e));
    // Prime the subscription cache so paywall checks are synchronous on routes.
    loadSubscription().catch((e) => console.warn("[app] loadSubscription:", e));
    // Load the user's emoji avatar, then repaint the chip.
    primeAvatar().then(renderChip).catch((e) => console.warn("[app] primeAvatar:", e));
    // lastSeen heartbeat — admin dashboard reads this for active-user counts.
    presence.start(user.uid);  // heartbeat — replaces one-shot writeLastSeen
    // If currently on sign-in or marketing page, go home.
    if (location.hash === "#/signin" || location.hash === "#/landing" || !location.hash) {
      navigate("/home");
    }
  } else {
    trackEvent("auth.signed-out");
    document.body.classList.add("signed-out");
    resetAvatar();
    chip.classList.add("hidden");
    chip.innerHTML = "";
    signout.classList.add("hidden");
    nav.classList.add("hidden");
    if (adminLink) adminLink.classList.add("hidden");
    resetAdminClaim();
    presence.stop();
    // Auth guard on initial boot + sign-out: any in-app route (anything
    // not in PUBLIC_ROUTES) gets redirected to /landing. Without this,
    // someone clicking an email link to a protected route (#/dashboard,
    // #/review, etc.) on a device where they're not signed in would
    // render the protected view, hit currentUser() === null inside
    // listBookmarks/allProgress, and crash with "Not signed in" — the
    // exact path that fired Sentry events. Sign-in remains reachable
    // as a CTA from the landing page so the user can complete the
    // round-trip back to whatever the email pointed them at.
    const path = location.hash.slice(1).split("?")[0] || "";
    const inPublic = PUBLIC_ROUTES.has(path) || path === "";
    if (!inPublic) navigate("/landing");
  }
});

// Auth guard: if a logged-out user navigates to any in-app route (e.g. by
// clicking the topbar brand link before it's been hidden, or by typing a
// hash directly), bounce them to /landing. Without this guard, /home would
// render its loading shell forever because there's no auth context.
window.addEventListener("hashchange", () => {
  if (currentUser()) return;
  const path = location.hash.slice(1).split("?")[0] || "/landing";
  if (!PUBLIC_ROUTES.has(path)) navigate("/landing");
});

// Register service worker (best-effort; fails silently on file:// origins)
if ("serviceWorker" in navigator && location.protocol !== "file:") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("./sw.js").catch((e) => console.warn("[sw] register failed:", e));
  });
}

// Boot
(async () => {
  if (OFFLINE_MODE) {
    showBanner("Running in offline mode — configure Firebase in js/firebase-config.js to enable accounts sync.");
  }

  // Path → hash conversion. Firebase Hosting rewrites every unknown path
  // to /index.html (so a deep URL like /admin/users serves the SPA), but
  // the SPA's hash router doesn't know about path-based deep links. If
  // the user landed on /admin/users with no hash, convert it to the
  // equivalent /#/admin/users so the right view renders + the URL stays
  // shareable. The explicit firebase.json redirects (/signin, /account,
  // /upgrade) still server-side-302 those paths first; this catch-all
  // handles every other deep URL on the client. We use replaceState so
  // the bad URL doesn't pollute Back.
  if (!OFFLINE_MODE
      && location.pathname !== "/"
      && location.pathname !== "/index.html"
      && !location.hash) {
    const hashTarget = "#" + location.pathname + location.search;
    history.replaceState(null, "", "/" + hashTarget);
  }

  await initAuth();
  start(currentUser() ? "/home" : "/landing");
  // Mobile-only "Install app" prompt. Safe to fire on every platform — the
  // module no-ops on desktop, on already-installed standalone sessions, and
  // within the 7-day cooldown after a user dismissal.
  initInstallPrompt();
  // Theme is initialised at theme.js load time. The picker lives on the
  // Account page now, so no toggle button to wire here.
  startAutoUpdate();
})();

function showBanner(msg) {
  const b = document.createElement("div");
  b.className = "banner";
  b.textContent = msg;
  document.body.insertBefore(b, document.getElementById("view"));
}

// Toast helper (exposed globally for view modules to use).
//
// Each call appends a NEW toast element to #toast-stack — multiple
// toasts can coexist (e.g. a save success + a streak milestone fire
// from different code paths within the same tick) and stack visually
// instead of clobbering each other.
//
// Variants:
//   "ok"     — green check, success states (saves, completions)
//   "warn"   — amber !, soft alerts (deck trimmed, recently active)
//   "bad"    — red ✕, errors (used as-is by older callers)
//   "error"  — alias for "bad"
//   "info"   — teal i, neutral info (default)
//
// Optional opts:
//   { duration } — ms before auto-dismiss. Default 3000ms; bad/error
//                  default to 4500ms so users have time to read errors.
//
// User can click a toast to dismiss it early.
const TOAST_ICONS = {
  ok:    "✓",
  warn:  "!",
  bad:   "✕",
  error: "✕",
  info:  "i",
};
const TOAST_DURATIONS = { bad: 4500, error: 4500 };
window.toast = function toast(msg, variant = "info", opts = {}) {
  const stack = document.getElementById("toast-stack");
  if (!stack || !msg) return;

  const v = TOAST_ICONS[variant] ? variant : "info";
  // Map "error" → "bad" CSS class so we don't have to duplicate styles.
  const cssVariant = v === "error" ? "bad" : v;

  const t = document.createElement("div");
  t.className = `toast toast-${cssVariant}`;
  t.setAttribute("role", "status");
  t.innerHTML = `
    <span class="toast-icon" aria-hidden="true">${TOAST_ICONS[v]}</span>
    <span class="toast-msg"></span>
  `;
  // textContent (not innerHTML) for the message — defends against any
  // accidental HTML in user-derived strings (error messages, names).
  t.querySelector(".toast-msg").textContent = String(msg);

  // Click anywhere to dismiss early.
  t.addEventListener("click", () => dismiss(t));

  stack.appendChild(t);

  // Trigger enter on the next frame so the from-state (opacity:0,
  // translateY) is committed before the to-state animates in.
  requestAnimationFrame(() => t.classList.add("toast-in"));

  // Auto-dismiss timer.
  const ms = Number(opts.duration) || TOAST_DURATIONS[v] || 3000;
  const tm = setTimeout(() => dismiss(t), ms);
  t._toastTm = tm;

  function dismiss(el) {
    if (!el || el._toastDismissed) return;
    el._toastDismissed = true;
    if (el._toastTm) clearTimeout(el._toastTm);
    el.classList.remove("toast-in");
    el.classList.add("toast-out");
    // Wait for the exit animation to finish before removing from DOM.
    setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 280);
  }
};

// Write the current user's lastSeen timestamp to Firestore. Lazy-imports the
// firestore SDK so the cost is paid only on a real signin, never on the
// landing page or signed-out state. Fail-soft: heartbeat is best-effort.
async function writeLastSeen(uid) {
  if (OFFLINE_MODE) return;
  const [{ getFirestore, doc, setDoc }, { getApp, getApps, initializeApp }, { firebaseConfig }] = await Promise.all([
    import("https://www.gstatic.com/firebasejs/10.12.0/firebase-firestore.js"),
    import("https://www.gstatic.com/firebasejs/10.12.0/firebase-app.js"),
    import("./firebase-config.js"),
  ]);
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const db = getFirestore(app);
  await setDoc(
    doc(db, "users", uid, "meta", "profile"),
    { lastSeen: Date.now() },
    { merge: true },
  );
}